import {
    EnterpriseUid,
    OrganizationUid,
    PrefixedUserUid,
    toPrefixedUserUid,
    UserUid,
} from "@buildwithflux/identifiers-schema/legacy";
import z from "zod";

import {DocIdSuperseded} from "./docIdSuperseded";

/**
 * Selective membership input shapes pulled into the permissions domain from
 * `@buildwithflux/models`'s `workspace/`. These are the *inputs* to the permission
 * algebra, so they belong with `schema` rather than the broader workspace bag
 * (which also contains payment / handle / api-key concerns that the permissions
 * algebra has no business knowing about).
 */

/**
 * Organization members can be owners
 */
export const OrganizationRole = z.enum(["owner", "member", "guest", "none"]);
export type OrganizationRole = z.infer<typeof OrganizationRole>;

// eslint-disable-next-line no-restricted-syntax
export function isOrganizationRole(role: unknown): role is OrganizationRole {
    return OrganizationRole.safeParse(role).success;
}

export const OrganizationVisibility = z.enum([
    /**
     * The original visibility setting for organizations: all memberships and details of the organization are public
     */
    "public",

    /**
     * A newer visibility setting initially used for demo organizations. Firestore rules prevent the memberships from
     * being queried and the profile page can only be seen by members. These organizations are not owned by an
     * enterprise, however, and participate in the same handle-namespace as public organizations. Their existence
     * can be detected by finding their user handle in the usernames collection.
     */
    "unlisted",

    /**
     * The newest visibility setting which indicates no details of this organization (including its handle) should be
     * able to be queried by non-members (where "non-members" may take into account membership of its parent enterprise)
     */
    "private",
]);
export type OrganizationVisibility = z.infer<typeof OrganizationVisibility>;

/**
 * Enterprise role is structurally identical to organization role.
 */
export const EnterpriseRole = OrganizationRole;
export type EnterpriseRole = OrganizationRole;

/**
 * A kind of joining table between users and organizations, this is pathed underneath the organization
 * to which it pertains in Firestore - the subcollection of organizationMembers is keyed by user_uid - important
 * for performing get() in security rules
 */
export interface OrganizationMember {
    /**
     * The user the membership pertains to, always in the canonical `usr_`-prefixed form.
     *
     * Persisted docs may still carry the bare Firebase auth UID; the membership read
     * chokepoint (`MembershipDocReader`) normalizes to the prefixed form on read.
     */
    userUid: PrefixedUserUid;

    /**
     * Whether the organization details and membership are unlisted or public
     *
     * This is directly denormalized from the organization itself so that we can secure membership queries in Firestore rules
     */
    visibility: OrganizationVisibility;

    /**
     * Whether the organization is owned by an enterprise
     *
     * This is directly denormalized from the organization itself so that we can perform authorization without considering
     * an additional relational links (user -> member -> enterprise instead of user -> member -> organization -> enterprise)
     *
     * Basically, we don't want to have to create additional membership records - we are reusing organization membership
     * to denote enterprise membership
     *
     * This field is truly optional: memberships before enterprises won't have this field at all, and queries looking
     * for enterpriseUid to be unset won't work.
     */
    enterpriseUid?: EnterpriseUid;

    /**
     * Handle of the user. This is also a unique field in user model, and we decide
     * to denormalize it, because we need it to perform sort when paging the member list
     */
    userHandle: string;

    /**
     * The organization the membership pertains to
     */
    organizationUid: OrganizationUid;

    /**
     * The role of the user in the organization - e.g. "owner" or "member"
     */
    role: OrganizationRole;

    /**
     * Timestamp in millis of when the organization membership was created
     */
    createdAt: number;

    /**
     * Timestamp in millis of when the organization membership was last modified
     */
    updatedAt: number;

    /**
     * Whether this member can withdraw their org from shared projects.
     * Optional, defaults to true when absent for backwards compatibility.
     * Org owners can always withdraw regardless of this setting.
     */
    can_leave_shares?: boolean;

    /**
     * Set when this membership doc was collapsed into its canonical (prefixed) sibling.
     * Marks the doc retired for readers and tells collapse-aware triggers to skip removal fan-out.
     */
    _docid_superseded?: DocIdSuperseded;
}

/**
 * The `organizationMembers` doc shape as persisted in Firestore.
 *
 * Unlike {@link OrganizationMember}, this makes no promise about the `userUid` form:
 * legacy docs carry the bare Firebase auth UID, newer docs the canonical `usr_`-prefixed
 * form (matching the doc id form in both cases). Read chokepoints normalize with
 * {@link toCanonicalOrganizationMember} before handing members to the rest of the app.
 */
export interface PersistedOrganizationMember extends Omit<OrganizationMember, "userUid"> {
    /** Bare (legacy) or `usr_`-prefixed user UID, matching the doc id form. */
    userUid: UserUid;
}

/**
 * Normalizes a persisted membership doc to the canonical `usr_`-prefixed `userUid` form,
 * making {@link OrganizationMember}'s prefixed promise true at the read boundary.
 */
export function toCanonicalOrganizationMember(member: PersistedOrganizationMember): OrganizationMember {
    return {...member, userUid: toPrefixedUserUid(member.userUid)};
}
